import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import isSameDay from 'react-dates/lib/utils/isSameDay'
import moment from 'moment'
import { Popover, RadioGroup } from '@junglescout/edna'

import { TextInput } from 'ui_elements/TextInput/TextInput'
import { CalendarIcon } from 'icons/CalendarIcon/CalendarIcon'
import { CaretIcon } from 'icons/CaretIcon/CaretIcon'
import { COLORS } from 'COLORS'
import { useClickOutside } from 'hooks/dom'
import { CalendarInput } from '../CalendarInput/CalendarInput'

import { PRODUCT_LAUNCH_DATE } from '../../../../constants/product_costs'

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  width: ${props => props.width};
`

const StyledCalendarInput = styled(CalendarInput)`
  width: 100%;
  margin-top: -5px;

  .SingleDatePicker {
    position: static;
  }

  .SingleDatePicker,
  .SingleDatePickerInput,
  .DateInput {
    width: 100%;
  }

  .DateInput::after {
    content: '';
  }

  .SingleDatePicker_picker {
    position: static;
    width: 100%;
  }

  .DayPicker {
    width: 100% !important;
    display: flex;
    justify-content: center;
  }
`

const StyledPopover = styled(Popover)`
  width: ${props => props.width || '305'}px;

  border: 1px solid ${props => props.theme.colors.coolGrey300};
  background: ${COLORS.white};
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);
  padding: 10px 15px;
`

const StyledRadioGroup = styled(RadioGroup)`
  padding-bottom: 15px;
  margin: 0 -5px;
`

const InnerWrapper = styled.div``

const Input = styled(TextInput)`
  max-height: 35px;
  min-height: 35px;
  font-size: 12px;
  width: 100%;
  padding-left: 30px;
`

const StyledCalendarIcon = styled(CalendarIcon)`
  position: absolute;
  top: 10px;
  left: 4px;
`

const StyledCaretIcon = styled(CaretIcon)`
  position: absolute;
  top: 16px;
  right: 6px;
`

const SELECT_DATE_OPTION = 'select-date'

export const CalendarInputOptionGroup = ({
  value,
  displayValue,
  onDatesChange,
  closeOnDatesChange,
  width,
  className,
  options,
  ...calendarProps
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [lastDate, setLastDate] = useState(moment())
  const [radioValue, setRadioValue] = useState(SELECT_DATE_OPTION)
  const { t } = useTranslation()

  useEffect(() => {
    setRadioValue(
      isSameDay(value, PRODUCT_LAUNCH_DATE)
        ? value.format('YYYY-MM-DD')
        : SELECT_DATE_OPTION
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isSameDay(value, PRODUCT_LAUNCH_DATE)) {
      setLastDate(value)
    }
  }, [value])

  const handleClickOutside = () => setIsOpen(false)
  const ref = useRef()

  useClickOutside(() => handleClickOutside(), [ref])

  const togglePopup = () => setIsOpen(!isOpen)

  const isSelectingDate = radioValue === SELECT_DATE_OPTION

  const handleDateChange = date => {
    if (closeOnDatesChange) {
      setIsOpen(false)
    }

    onDatesChange(date)
  }

  const handleRadioGroupChange = val => {
    const newDate = val === SELECT_DATE_OPTION ? lastDate : moment(val)
    handleDateChange(newDate)
    setRadioValue(val)
  }

  return (
    <Wrapper
      width={width}
      className={`calendar-wrapper ${className}`}
      ref={ref}>
      <StyledPopover
        width={width}
        isSelectingDate={isSelectingDate}
        className="calendar-popup"
        content={
          <InnerWrapper>
            <StyledRadioGroup
              name="calendar-input"
              value={radioValue}
              onChange={handleRadioGroupChange}
              options={[
                ...options,
                {
                  label: t(
                    'sales_analytics:expenses.ToggleExpenseState.selecteDate',
                    'Select Date'
                  ),
                  value: 'select-date'
                }
              ]}
            />
            {isSelectingDate && (
              <StyledCalendarInput
                {...calendarProps}
                // value={value}
                onDatesChange={handleDateChange}
                focusedInput
                displayFormat="MMM DD, YYYY"
                className="calendar-input"
              />
            )}
          </InnerWrapper>
        }>
        <div>
          <StyledCalendarIcon onClick={togglePopup} />
          <Input
            onClick={togglePopup}
            value={displayValue}
            onChange={() => null}
            onkeypress="return false;"
          />
          <StyledCaretIcon
            width={8}
            height={8}
            color="grey900"
            rotate={90}
            onClick={togglePopup}
          />
        </div>
      </StyledPopover>
    </Wrapper>
  )
}

CalendarInputOptionGroup.defaultProps = {
  width: '275px',
  closeOnDatesChange: false
}

CalendarInputOptionGroup.propTypes = {
  ...CalendarInput.propTypes,
  displayValue: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ).isRequired,
  width: PropTypes.string,
  closeOnDatesChange: PropTypes.bool
}
